import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import TimerEvent from "../../timer/event/TimerEvent";
import HubKeyDecisionListItemFactory from "../controller/HubKeyDecisionListItemFactory";
import HubKeyDecisionListItemMediator from "./HubKeyDecisionListItemMediator";

export default class HubKeyDecisionListMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     * @param {HubKeyDecisionListItemFactory} hubKeyDecisionListItemFactory 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, hubKeyDecisionListItemFactory, keyDecisionsConfig) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._hubKeyDecisionListItemFactory = hubKeyDecisionListItemFactory;
        this._keyDecisionsConfig = keyDecisionsConfig;

        /** @type {Array<HubKeyDecisionListItemMediator>} */
        this._listItems = [];
        this._enableFeedbackMode = false;
    }

    initialize() {
        super.initialize();

        this._target.removeAllChildren();
    }

    destroy() {
        super.destroy();
        this.clarKeyDecisions();
        this._target.removeAllChildren();
    }

    clarKeyDecisions() {
        for (let i = 0; i < this._listItems.length; i++) {
            const listItem = this._listItems[i];
            listItem.destroy();
        }
        this._listItems = [];
    }

    initializeKeyDecisions(number) {
        for (let i = 0; i < number; i++) {
            let keyDecisionListItemMediator;
            if (this._enableFeedbackMode) {
                keyDecisionListItemMediator = this._hubKeyDecisionListItemFactory.createFeedbackLockedKeyDecisionListItemKey(this._target, i);
            } else {
                keyDecisionListItemMediator = this._hubKeyDecisionListItemFactory.createHubLockedKeyDecisionListItemKey(this._target);
            }
            this._listItems.push(keyDecisionListItemMediator);
        }
        this.updateItemPositions();
    }

    makeFirstKeyDecisionSelected() {
        this._listItems[0].selected();
    }

    selectKeyDecision(index) {
        for (let i = 0; i < this._listItems.length; i++) {
            if (i == index) {
                this._listItems[i].selected();
            } else {
                this._listItems[i].availableSeen();
            }
        }
    }

    makeKeyDecisionAvailableForFeedback(keyDecision) {
        const firstKeyDecisionSlotAvailable = this._listItems.find(kd => !kd.hasKeyDecision());
        firstKeyDecisionSlotAvailable.initializeWithKeyDecision(keyDecision);
        firstKeyDecisionSlotAvailable.availableSeen();
        this.updateItemPositions();
    }

    makeKeyDecisionAvailable(keyDecision) {
        const firstKeyDecisionSlotAvailable = this._listItems.find(kd => !kd.hasKeyDecision());
        firstKeyDecisionSlotAvailable.initializeWithKeyDecision(keyDecision);
        firstKeyDecisionSlotAvailable.availableUnseen();
        this.updateItemPositions();
        this._dispatcher.dispatch(TimerEvent.START_TIMER);
    }

    makeKeyDecisionPerformed(keyDecision) {
        const firstKeyDecisionSlotAvailable = this._listItems.find(kd => kd.getKeyDecision() == keyDecision);
        firstKeyDecisionSlotAvailable.performed();
        this.updateItemPositions();
        if(this.currentArc != keyDecision.arc) {
            this.currentArc = keyDecision.arc;
            this.expectedNumberKeyDecisionCurrentArc = this._keyDecisionsConfig.getNumberKeyDecisionsPerArc(keyDecision.chapter, this.currentArc);
            this.currentNumberKeyDecisionCurrentArc = 1;
        }
        if(this.currentNumberKeyDecisionCurrentArc < this.expectedNumberKeyDecisionCurrentArc) {
            ++this.currentNumberKeyDecisionCurrentArc;
        } else {
            this._dispatcher.dispatch(TimerEvent.STOP_TIMER);
        }
    }

    addKeyDecision(keyDecision) {
        const keyDecisionListItemMediator = this._hubKeyDecisionListItemFactory.createKeyDecisionListItem(this._target, keyDecision);
        this._listItems.push(keyDecisionListItemMediator);

        // Animate in the new item
        keyDecisionListItemMediator.skin.x = 100;
        keyDecisionListItemMediator.skin.y = this._getNextItemY();
        keyDecisionListItemMediator.skin.alpha = 0;

        createjs.Tween.get(keyDecisionListItemMediator.skin)
            .to({ alpha: 1, x: 0 }, 650, createjs.Ease.sineInOut);

        this.updateItemPositions();
    }

    removeKeyDecision(keyDecision) {
        const listItemToRemoveIndex = this._listItems.findIndex(i => i.keyDecision === keyDecision);
        const listItemToRemove = this._listItems[listItemToRemoveIndex];
        this._listItems.splice(listItemToRemoveIndex, 1)

        createjs.Tween.get(listItemToRemove.skin)
            .to({ alpha: 0, x: 100 }, 650, createjs.Ease.sineInOut);

        this.updateItemPositions();
    }

    updateItemPositions() {
        let nextY = (this._listItems.length >= 5 ? HubKeyDecisionListMediator.OFFSET_5_KEY_DECISIONS : 0);
        let itemSpacingA = this._enableFeedbackMode ? HubKeyDecisionListMediator.FEEDBACK_ITEM_SPACING : HubKeyDecisionListMediator.ITEM_SPACING;
        let itemSpacingAvailable = this._enableFeedbackMode ? HubKeyDecisionListMediator.FEEDBACK_ITEM_SPACING_AVAILABLE : HubKeyDecisionListMediator.ITEM_SPACING_AVAILABLE;

        for (let i = 0; i < this._listItems.length; i++) {
            const listItem = this._listItems[i];
            const itemSpacing = (listItem.isAvailable() ? itemSpacingAvailable : itemSpacingA);
            createjs.Tween.get(listItem.skin)
                .to({ y: nextY }, 650, createjs.Ease.sineInOut);

            nextY += itemSpacing;
        }
    }

    enableFeedbackMode() {
        this._enableFeedbackMode = true;
    }

    _getNextItemY() {
        let itemSpacing = this._enableFeedbackMode ? HubKeyDecisionListMediator.FEEDBACK_ITEM_SPACING : HubKeyDecisionListMediator.ITEM_SPACING;

        const totalHeight = itemSpacing * this._listItems.length;
        const startingY = -totalHeight * .5 + itemSpacing * .5;

        return startingY + itemSpacing * (this._listItems.length - 1);
    }
}

HubKeyDecisionListMediator.ITEM_SPACING = 90;
HubKeyDecisionListMediator.ITEM_SPACING_AVAILABLE = 100;

HubKeyDecisionListMediator.FEEDBACK_ITEM_SPACING = 55;
HubKeyDecisionListMediator.FEEDBACK_ITEM_SPACING_AVAILABLE = 120;

HubKeyDecisionListMediator.OFFSET_5_KEY_DECISIONS = -85;